import { MappedField, ProgramAttendeeCreateParams } from '@citruscamps/citrus-client'
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormikErrors, FormikTouched, useFormik } from 'formik'
import React from 'react'
import { NavLink } from 'react-router-dom'
import CreatableSelect from 'react-select/creatable'
import * as Yup from 'yup'
import { FieldCarousel } from '../../../../../components/FieldCarousel/FieldCarousel'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { TruncatedText } from '../../../../../components/TruncatedText/TruncatedText'
import { ROUTES } from '../../../../../constants/routes'
import translations from '../../../../../translations/en.json'
import { getFieldSelectClasses } from '../../../../../utils/branding-utils'

interface MappedFieldInput extends MappedField {
  applied?: boolean
}

const mergeFields: any = {
  'attendee.first_name': 'First name',
  'attendee.last_name': 'Last name',
  'attendee.email': 'Email address',
  'attendee.phone': 'Phone number',
  'attendee.date_of_birth': 'Date of Birth',
  'attendee.gender': 'Gender',
  'attendee.health_card_number': 'Health card',
  'attendee.primary_contact_email': "Contact's email",
  'attendee.parent_name': "Contact's name",
}

const mappedFieldsToAttendees = (
  fields: MappedFieldInput[],
  program_id: string,
): ProgramAttendeeCreateParams[] => {
  const attendees: ProgramAttendeeCreateParams[] = []
  fields.forEach((field) => {
    field.values.forEach((value, i) => {
      if (!attendees?.[i]) {
        attendees.push({} as ProgramAttendeeCreateParams)
      }
      if (
        field.merge_id &&
        Object.keys(mergeFields).includes(field.merge_id) &&
        field.merge_id.includes('attendee.')
      ) {
        attendees[i] = {
          ...(attendees[i] || {}),
          [field.merge_id.split('.').pop() || '']: value,
          program_id,
        }
      } else {
        attendees[i] = {
          ...(attendees[i] || {}),
          properties: {
            ...(attendees[i].properties || {}),
            ...(field.merge_id ? { [field.merge_id || field.mapped_id]: value } : {}),
          },
          program_id,
        }
      }
    })
  })
  return attendees
}

const mapMergeFieldOption = (value: string): { label: string; value: string } => ({
  value,
  label: mergeFields[value] || value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
})

const invalidFeedback = (
  key: keyof IValue,
  touched: FormikTouched<IValue>,
  errors: FormikErrors<IValue>,
) =>
  touched[key] && !!errors[key] && typeof errors[key] === 'string' ? (errors[key] as string) : null

interface IValue {
  fields: MappedFieldInput[]
}

interface IProps {
  initialValues?: IValue
  programId: string
  onBack?: () => Promise<void> | void
  onSubmit: (
    values: ProgramAttendeeCreateParams[],
    fields: MappedFieldInput[],
  ) => Promise<void> | void
}

export const AttendeeMatchStep = ({
  initialValues,
  programId,
  onBack: handleBack,
  onSubmit,
}: IProps) => {
  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldError,
    setFieldValue,
    touched,
    values,
  } = useFormik<IValue>({
    initialValues: {
      fields:
        initialValues?.fields.map((f) => ({ ...f, applied: !!f.applied || !!f.merge_id })) || [],
    },
    validationSchema: Yup.object().shape({
      fields: Yup.array<MappedFieldInput>()
        .test(
          'first_name',
          (translations as any)['error.fields_first_name_col_required'],
          (val) => !!val?.some((v) => v?.merge_id === 'attendee.first_name'),
        )
        .test(
          'primary_contact_email',
          (translations as any)['error.fields_primary_contact_email_col_required'],
          (val) => !!val?.some((v) => v?.merge_id === 'attendee.primary_contact_email'),
        ),
    }),
    onSubmit: async (value, { setSubmitting }) => {
      try {
        await onSubmit(
          mappedFieldsToAttendees(
            value.fields.filter((f) => f.applied),
            programId,
          ),
          values.fields,
        )
      } catch (e: any) {
        if (e.param) setFieldError(e.param, e.message)
      }
      setSubmitting(false)
    },
  })
  return (
    <form className="py-3" onSubmit={handleSubmit}>
      <h5 className="mb-4">Match labels to attendees information</h5>
      <p>
        <b>{values.fields?.[0]?.values?.length || 0}</b> attendees were recognized in this file
      </p>
      <div className="mb-3">
        {values.fields.length > 0 && (
          <FieldCarousel className="mb-3" length={values.fields.length}>
            {values.fields.map((field, i) => (
              <ul className="list-group" key={['fields', i].join(':')} style={{ minHeight: 250 }}>
                <li
                  className={`list-group-item text-capitalize ps-2${
                    !field.merge_id ? ' list-group-item-danger' : ''
                  }`}
                >
                  <div className="input-group">
                    <CreatableSelect
                      inputId={`fields[${i}].merge_id`}
                      placeholder="Unknown"
                      onChange={(val: any) => {
                        values.fields.forEach((f, j) => {
                          if (f.merge_id === val.value)
                            setFieldValue(`fields[${j}].merge_id`, undefined, true)
                        })
                        setFieldValue(`fields[${i}].merge_id`, val.value, true)
                        setFieldValue(`fields[${i}].applied`, true, true)
                      }}
                      value={
                        typeof field.merge_id !== 'undefined'
                          ? mapMergeFieldOption(field.merge_id)
                          : null
                      }
                      options={Object.keys(mergeFields).map(mapMergeFieldOption)}
                      formatOptionLabel={(option) => option.label}
                      formatCreateLabel={(val) => `${val} (New field)`}
                      classNames={getFieldSelectClasses()}
                      unstyled
                    />
                    <div className="input-group-text border-0 bg-transparent pe-0">
                      {field.merge_id ? (
                        <input
                          name={`fields[${i}].applied`}
                          type="checkbox"
                          className="form-check-input ms-2"
                          aria-label="Import field"
                          checked={field.applied}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      ) : (
                        <FontAwesomeIcon className="text-danger " icon={faExclamationCircle} />
                      )}
                    </div>
                  </div>
                </li>
                {field.values.slice(0, 5).map((val, j) => (
                  <li
                    className={`list-group-item${!field.merge_id ? ' list-group-item-danger' : ''}`}
                    key={['field', i, j].join(':')}
                  >
                    <TruncatedText>{val || '\u00A0'}</TruncatedText>
                  </li>
                ))}
              </ul>
            ))}
          </FieldCarousel>
        )}
        <div className="invalid-feedback d-block text-body-secondary text-md-start mt-3 text-center">
          {values.fields.filter((f) => f.merge_id && f.applied).length} columns will be imported.{' '}
          {values.fields.filter((f) => !f.merge_id || !f.applied).length} columns will not be
          imported.
        </div>
        <div className="invalid-feedback d-block">
          {invalidFeedback('fields', touched, errors) || '\u00A0'}
        </div>
      </div>
      <div className="mb-3 d-flex justify-content-between mt-4">
        {handleBack ? (
          <button
            type="button"
            className="btn btn-link btn-block d-md-inline-block w-md-auto mt-2 px-0"
            onClick={() => handleBack?.()}
          >
            Back
          </button>
        ) : (
          <NavLink
            className="btn btn-link btn-block d-md-inline-block w-md-auto mt-2"
            to={ROUTES.CONTACTS}
          >
            Cancel
          </NavLink>
        )}
        <LoadingButton
          type="submit"
          className="btn btn-primary btn-block d-md-inline-block w-md-auto"
          loading={isSubmitting}
        >
          Continue
        </LoadingButton>
      </div>
    </form>
  )
}
